import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { orchestratorHelpPtBR } from '../../i18n/iaSettingsOrchestratorHelp.pt-BR';
import {
  AISettings,
  AIRole,
  AIRoleTemplate,
  Guideline,
  CreateGuidelineRequest,
  CreateAIRoleRequest,
  AIProvider,
  AITom,
  GuidelineTipo,
  PriceSource,
  CreatePriceSourceRequest,
  RagWebPage,
  CreateRagWebPageRequest,
  PromptCatalogItem,
  SpecialistPrompt,
  ProvidersByTier,
} from '../../types';
import './AISettingsTab.css';
import { SdrSettingsPanel } from './SdrSettingsPanel';
import { PerTierConfigSection } from './PerTierConfigSection';
import { AtendenteIaInlineTab } from './AtendenteIaInlineTab';
import { CatalogTab } from './CatalogTab';

interface AISettingsTabProps {
  isOwner: boolean;
  isAdmin: boolean;
  isGerente: boolean;
  organizationId: string;
}

type AISubTab =
  | 'config'
  | 'roles'
  | 'guidelines'
  | 'catalog'
  | 'web_pages'
  | 'price_sources'
  | 'orchestrator_prompts'
  | 'sdr'
  | 'atendente_ia_sdr';

/**
 * OpenSpec unify-ia-settings-and-retire-legacy-sdr (Fase 1).
 * Default ON nesta PR; admin desabilita via env `REACT_APP_IA_SETTINGS_UNIFIED_UI_ENABLED=false`
 * caso precise voltar pro layout legacy (Identidade IA + Roteiro comercial + SDR Funil IA).
 * Quando ON:
 *   - Tabs "Identidade da IA" e "SDR (Funil IA)" ocultas.
 *   - "Roteiro comercial" renomeado pra "Guidelines (RAG)".
 *   - Tab nova "Atendente IA & SDR" renderiza AtendenteIaInlineTab (sidebar 2 itens).
 *   - Banner "Abrir nova UI →" oculto.
 */
const IA_SETTINGS_UNIFIED_UI_ENABLED =
  (process.env.REACT_APP_IA_SETTINGS_UNIFIED_UI_ENABLED || 'true').toLowerCase() !== 'false';

/**
 * OpenSpec add-categorized-catalog-guideline — feature flag pra UI Catálogo.
 * Default OFF até PR 2 Fase 5. Setar 'true' habilita a tab nova.
 */
const CATALOG_RAG_ENABLED =
  (process.env.REACT_APP_TENANT_CATALOG_RAG_ENABLED || 'false').toLowerCase() === 'true';

export const AISettingsTab: React.FC<AISettingsTabProps> = ({
  isOwner,
  isAdmin,
  isGerente,
  organizationId,
}) => {
  const { refreshOrganization } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState<AISubTab>('config');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Data states
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
const [aiSettings, setAiSettings] = useState<AISettings | null>(null);
  const [roles, setRoles] = useState<AIRole[]>([]);
  const [roleTemplates, setRoleTemplates] = useState<AIRoleTemplate[]>([]);
  const [guidelines, setGuidelines] = useState<Guideline[]>([]);
  const [priceSources, setPriceSources] = useState<PriceSource[]>([]);
  const [ragWebPages, setRagWebPages] = useState<RagWebPage[]>([]);
  const [promptCatalog, setPromptCatalog] = useState<PromptCatalogItem[]>([]);
  const [activeSpecialists, setActiveSpecialists] = useState<SpecialistPrompt[]>([]);
  const [promptEditor, setPromptEditor] = useState<{
    overridePrompt: string;
    examplesPositive: string;
    examplesNegative: string;
    threshold: string;
  }>({
    overridePrompt: '',
    examplesPositive: '',
    examplesNegative: '',
    threshold: '',
  });

  const canManageExtended = isOwner || isAdmin || isGerente;
  const canEditSdr = isOwner || isAdmin;

  // Modal states
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showAddGuidelineModal, setShowAddGuidelineModal] = useState(false);
  const [showEditGuidelineModal, setShowEditGuidelineModal] = useState(false);
  const [orchestratorHelpDrawerOpen, setOrchestratorHelpDrawerOpen] = useState(false);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideBase, setOverrideBase] = useState<SpecialistPrompt | null>(null);
  const [overrideForm, setOverrideForm] = useState<{ name: string; description: string; promptText: string }>({
    name: '',
    description: '',
    promptText: '',
  });

  // Form states - AI Settings
  const [editingProvider, setEditingProvider] = useState<AIProvider>('openai');
  const [editingModel, setEditingModel] = useState('gpt-4o-mini');
  const [editingTemperature, setEditingTemperature] = useState(0.7);
  const [editingTom, setEditingTom] = useState<AITom>('profissional');
  const [editingEnabled, setEditingEnabled] = useState(false);
  // OpenSpec #2.7 — per-tier provider/model overrides (optional)
  const [editingProvidersByTier, setEditingProvidersByTier] = useState<ProvidersByTier | undefined>(undefined);

  // Form states - Role
  const [selectedRole, setSelectedRole] = useState<AIRole | null>(null);
  const [newRoleForm, setNewRoleForm] = useState<CreateAIRoleRequest>({
    nome: '',
    descricao: '',
    systemPrompt: '',
    tom: 'profissional',
    exemplos: [],
    isDefault: false,
  });

  // Form states - Guideline
  const [selectedGuideline, setSelectedGuideline] = useState<Guideline | null>(null);
  const [newGuidelineForm, setNewGuidelineForm] = useState<CreateGuidelineRequest>({
    titulo: '',
    tipo: 'atendimento',
    conteudo: '',
  });
  const [guidelineFile, setGuidelineFile] = useState<File | null>(null);
  const [addGuidelineFileInputKey, setAddGuidelineFileInputKey] = useState(0);
  const [editGuidelineFileInputKey, setEditGuidelineFileInputKey] = useState(0);

  const [newPriceSourceForm, setNewPriceSourceForm] = useState<CreatePriceSourceRequest>({
    nome: '',
    tipo: 'website',
    prioridade: 0,
    ativo: true,
    allowedHosts: [],
    guidelineId: '',
  });
  const [priceHostsInput, setPriceHostsInput] = useState('');
  /** Fonte de preço tipo guideline: texto do autocomplete (nome) + lista */
  const [priceSourceGuidelineCombo, setPriceSourceGuidelineCombo] = useState('');
  const [priceSourceGuidelineListOpen, setPriceSourceGuidelineListOpen] = useState(false);
  const priceSourceGuidelineWrapRef = useRef<HTMLDivElement>(null);

  const [newWebPageForm, setNewWebPageForm] = useState<CreateRagWebPageRequest>({
    nome: '',
    url: '',
    allowedHosts: [],
    ativo: true,
  });
  const [webPageHostsInput, setWebPageHostsInput] = useState('');
  const [editingFunilVisualizacao, setEditingFunilVisualizacao] = useState<'physics' | 'slides'>('physics');
  const [editingHandoffBinsUi, setEditingHandoffBinsUi] = useState(false);
  const [funilViewSaving, setFunilViewSaving] = useState(false);
  /** Exemplo longo de domínio para perfis — vem do GET /orchestrator-prompts/catalog (`adminHints`). */
  const [profileDomainExampleFromCatalog, setProfileDomainExampleFromCatalog] = useState('');

  const canManageFunilVisualizacao = isOwner || isAdmin;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Phase 1 (critical): unblock first paint of IA settings tab.
      const [settingsData, orgData] = await Promise.all([
        apiService.getAISettings(organizationId).catch(() => null),
        apiService.getOrganization(organizationId).catch(() => null),
      ]);

      setAiSettings(settingsData);
      if (orgData) {
        setEditingFunilVisualizacao(orgData.funilVisualizacao === 'slides' ? 'slides' : 'physics');
      }

      // Initialize form with current settings
      if (settingsData) {
        setEditingProvider(settingsData.provider);
        setEditingModel(settingsData.model || 'gpt-4o-mini');
        setEditingTemperature(settingsData.temperature ?? 0.7);
        setEditingTom(settingsData.defaultTom || 'profissional');
        setEditingEnabled(settingsData.enabled);
        setEditingHandoffBinsUi(!!settingsData.handoffBinsUi);
        setEditingProvidersByTier(settingsData.providersByTier);
      }

      // Phase 2 (non-critical): load remaining resources in background.
      void (async () => {
        const [rolesData, templatesData, guidelinesData, rwpData, psData, promptCatalogResponse, specialistsData] =
          await Promise.all([
            apiService.getAIRoles().catch(() => []),
            apiService.getAIRoleTemplates().catch(() => []),
            apiService.getGuidelines().catch(() => []),
            canManageExtended ? apiService.getRagWebPages().catch(() => []) : Promise.resolve([]),
            canManageExtended ? apiService.getPriceSources().catch(() => []) : Promise.resolve([]),
            canManageExtended
              ? apiService
                  .getOrchestratorPromptCatalog()
                  .catch(() => ({ prompts: [], adminHints: { profileDomainExample: '' } }))
              : Promise.resolve({ prompts: [], adminHints: { profileDomainExample: '' } }),
            canManageExtended ? apiService.getActiveSpecialistPrompts().catch(() => []) : Promise.resolve([]),
          ]);
        setRoles(rolesData || []);
        setRoleTemplates(templatesData || []);
        setGuidelines(guidelinesData || []);
        setRagWebPages(rwpData || []);
        setPriceSources(psData || []);
        const catalogPrompts = promptCatalogResponse?.prompts || [];
        setPromptCatalog(catalogPrompts);
        setProfileDomainExampleFromCatalog(promptCatalogResponse?.adminHints?.profileDomainExample || '');
        setActiveSpecialists(specialistsData || []);
      })();
    } catch (err: any) {
      console.error('Erro ao carregar configurações de IA:', err);
      setError('Erro ao carregar configurações de IA');
    } finally {
      setLoading(false);
    }
  }, [organizationId, canManageExtended]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!orchestratorHelpDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOrchestratorHelpDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [orchestratorHelpDrawerOpen]);

  const refreshRoles = useCallback(async () => {
    const list = await apiService.getAIRoles().catch(() => []);
    setRoles(list || []);
  }, []);

  const defaultProfileForOrchestrator = useMemo(
    () => roles.find((r) => r.isDefault),
    [roles]
  );

  // AI Settings handlers
  const handleSaveFunilVisualizacao = async () => {
    if (!canManageFunilVisualizacao) return;
    try {
      setFunilViewSaving(true);
      await apiService.updateOrganization(organizationId, {
        funilVisualizacao: editingFunilVisualizacao,
      });
      await apiService.updateAISettings(organizationId, {
        handoffBinsUi: editingHandoffBinsUi,
      });
      await refreshOrganization();
      alert('Visualização do Funil IA salva.');
    } catch (err: any) {
      console.error('Erro ao salvar visualização do Funil IA:', err);
      alert(err.response?.data?.message || 'Erro ao salvar visualização do Funil IA');
    } finally {
      setFunilViewSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setActionLoading(true);
      const updated = await apiService.updateAISettings(organizationId, {
        provider: editingProvider,
        model: editingModel,
        temperature: editingTemperature,
        defaultTom: editingTom,
        enabled: editingEnabled,
        providersByTier: editingProvidersByTier,
      });
      setAiSettings(updated);
      alert('Configurações salvas com sucesso!');
    } catch (err: any) {
      console.error('Erro ao salvar configurações:', err);
      alert(err.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setActionLoading(false);
    }
  };

  // Role handlers
  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      await apiService.createAIRole(newRoleForm);
      await refreshRoles();
      setShowAddRoleModal(false);
      setNewRoleForm({
        nome: '',
        descricao: '',
        systemPrompt: '',
        tom: 'profissional',
        exemplos: [],
        isDefault: false,
      });
    } catch (err: any) {
      console.error('Erro ao criar role:', err);
      alert(err.response?.data?.message || 'Erro ao criar perfil de IA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditRole = (role: AIRole) => {
    setSelectedRole(role);
    setNewRoleForm({
      nome: role.nome,
      descricao: role.descricao,
      systemPrompt: role.systemPrompt,
      tom: role.tom,
      exemplos: role.exemplos || [],
      isDefault: role.isDefault,
    });
    setShowEditRoleModal(true);
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) return;

    try {
      setActionLoading(true);
      await apiService.updateAIRole(selectedRole.roleId, newRoleForm);
      await refreshRoles();
      setShowEditRoleModal(false);
      setSelectedRole(null);
    } catch (err: any) {
      console.error('Erro ao atualizar role:', err);
      alert(err.response?.data?.message || 'Erro ao atualizar perfil de IA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este perfil de IA?')) return;

    try {
      setActionLoading(true);
      await apiService.deleteAIRole(roleId);
      setRoles(roles.filter(r => r.roleId !== roleId));
    } catch (err: any) {
      console.error('Erro ao excluir role:', err);
      alert(err.response?.data?.message || 'Erro ao excluir perfil de IA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUseTemplate = (template: AIRoleTemplate) => {
    setNewRoleForm({
      nome: template.nome,
      descricao: template.descricao,
      systemPrompt: template.systemPrompt,
      tom: template.tom,
      exemplos: template.exemplos || [],
      isDefault: false,
    });
    setShowAddRoleModal(true);
  };

  // Guideline handlers
  const handleCreateGuideline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guidelineFile && !newGuidelineForm.conteudo?.trim()) {
      alert('Informe o conteúdo na caixa de texto ou selecione um arquivo (PDF, TXT ou DOCX).');
      return;
    }
    try {
      setActionLoading(true);
      
      let newGuideline: Guideline;
      
      if (guidelineFile) {
        // Upload file to S3 first, then create guideline
        newGuideline = await apiService.createGuidelineWithFile(
          {
            titulo: newGuidelineForm.titulo,
            tipo: newGuidelineForm.tipo,
          },
          guidelineFile
        );
      } else {
        // Create guideline with text content only
        newGuideline = await apiService.createGuideline({
          ...newGuidelineForm,
        });
      }
      
      setGuidelines([newGuideline, ...guidelines.filter((g) => g.guidelineId !== newGuideline.guidelineId)]);
      setShowAddGuidelineModal(false);
      setNewGuidelineForm({
        titulo: '',
        tipo: 'atendimento',
        conteudo: '',
      });
      setGuidelineFile(null);

      try {
        await apiService.indexGuideline(newGuideline.guidelineId);
        const fresh = await apiService.getGuideline(newGuideline.guidelineId);
        setGuidelines((prev) => prev.map((g) => (g.guidelineId === fresh.guidelineId ? fresh : g)));
      } catch (indexErr) {
        console.error(indexErr);
        alert(
          `Guideline salvo, mas a indexação no Pinecone falhou: ${indexErrorMessage(indexErr)}. ` +
            'Use «Indexar agora» no card para tentar de novo (veja também os logs do ai-service).'
        );
      }
    } catch (err: any) {
      console.error('Erro ao criar guideline:', err);
      alert(err.response?.data?.message || 'Erro ao criar guideline');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditGuideline = (guideline: Guideline) => {
    setSelectedGuideline(guideline);
    setGuidelineFile(null);
    setEditGuidelineFileInputKey((k) => k + 1);
    setNewGuidelineForm({
      titulo: guideline.titulo,
      tipo: guideline.tipo,
      conteudo: guideline.conteudo || '',
    });
    setShowEditGuidelineModal(true);
  };

  const handleUpdateGuideline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGuideline) return;

    try {
      setActionLoading(true);
      
      let conteudo = newGuidelineForm.conteudo;
      if (guidelineFile) {
        conteudo = await guidelineFile.text();
      }

      const updated = await apiService.updateGuideline(selectedGuideline.guidelineId, {
        ...newGuidelineForm,
        conteudo,
      });
      
      // Re-index the guideline
      await apiService.indexGuideline(selectedGuideline.guidelineId);
      
      setGuidelines(guidelines.map(g => g.guidelineId === selectedGuideline.guidelineId ? updated : g));
      setShowEditGuidelineModal(false);
      setSelectedGuideline(null);
      setGuidelineFile(null);
    } catch (err: any) {
      console.error('Erro ao atualizar guideline:', err);
      alert(err.response?.data?.message || 'Erro ao atualizar guideline');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGuideline = async (guidelineId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este guideline?')) return;

    try {
      setActionLoading(true);
      await apiService.deleteGuideline(guidelineId);
      setGuidelines(guidelines.filter(g => g.guidelineId !== guidelineId));
    } catch (err: any) {
      console.error('Erro ao excluir guideline:', err);
      alert(err.response?.data?.message || 'Erro ao excluir guideline');
    } finally {
      setActionLoading(false);
    }
  };

  const getProviderModels = (provider: AIProvider): string[] => {
    switch (provider) {
      case 'openai':
        return ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo', 'o1'];
      case 'azure':
        return ['gpt-4', 'gpt-4-turbo', 'gpt-35-turbo', 'gpt-4o', 'gpt-4o-mini'];
      case 'anthropic':
        return ['claude-3-5-haiku-20241022', 'claude-3-5-sonnet-20241022', 'claude-3-opus-20240229'];
      case 'google':
        return ['gemini-1.5-flash', 'gemini-1.5-pro', 'gemini-pro'];
      case 'bedrock':
        return [
          'anthropic.claude-haiku-4-5-20251001-v1:0',
          'anthropic.claude-sonnet-4-6-v1:0',
          'anthropic.claude-opus-4-7-v1:0',
          'anthropic.claude-3-5-haiku-20241022-v1:0',
          'anthropic.claude-3-5-sonnet-20241022-v2:0',
        ];
      default:
        return [];
    }
  };

  const getTomLabel = (tom: AITom): string => {
    switch (tom) {
      case 'profissional': return 'Profissional';
      case 'amigavel': return 'Amigável';
      case 'formal': return 'Formal';
      case 'casual': return 'Casual';
      case 'tecnico': return 'Técnico';
      default: return tom;
    }
  };

  const getTipoLabel = (tipo: GuidelineTipo | 'outro'): string => {
    switch (tipo) {
      case 'atendimento': return 'Atendimento';
      case 'produto': return 'Produto';
      case 'curso': return 'Curso';
      case 'preco': return 'Preço';
      case 'servico': return 'Serviço';
      case 'outro': return 'Serviço';
      default: return tipo;
    }
  };

  const indexErrorMessage = (err: unknown): string => {
    const ax = err as { response?: { data?: { message?: string; error?: string } } };
    return (
      ax.response?.data?.message ||
      ax.response?.data?.error ||
      (err instanceof Error ? err.message : 'Erro desconhecido')
    );
  };

  const handleRetryIndexGuideline = async (guidelineId: string) => {
    try {
      setActionLoading(true);
      await apiService.indexGuideline(guidelineId);
      const fresh = await apiService.getGuideline(guidelineId);
      setGuidelines((prev) => prev.map((g) => (g.guidelineId === guidelineId ? fresh : g)));
    } catch (err) {
      console.error(err);
      alert(`Indexação falhou: ${indexErrorMessage(err)}`);
    } finally {
      setActionLoading(false);
    }
  };

  const priceSourceGuidelineOptions = useMemo(() => {
    const q = priceSourceGuidelineCombo.trim().toLowerCase();
    const base = q
      ? guidelines.filter((g) => g.titulo.toLowerCase().includes(q))
      : guidelines;
    return base.slice(0, 40);
  }, [guidelines, priceSourceGuidelineCombo]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (
        priceSourceGuidelineWrapRef.current &&
        !priceSourceGuidelineWrapRef.current.contains(e.target as Node)
      ) {
        setPriceSourceGuidelineListOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectPriceSourceGuideline = (g: Guideline) => {
    setNewPriceSourceForm((f) => ({ ...f, guidelineId: g.guidelineId }));
    setPriceSourceGuidelineCombo(g.titulo);
    setPriceSourceGuidelineListOpen(false);
  };

  const handleCreatePriceSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPriceSourceForm.nome.trim()) {
      alert('Informe o nome da fonte');
      return;
    }
    if (newPriceSourceForm.tipo === 'guideline' && !newPriceSourceForm.guidelineId?.trim()) {
      alert('Escolha uma guideline pelo nome na lista.');
      return;
    }
    try {
      setActionLoading(true);
      const hosts = priceHostsInput
        .split(/[,\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const body: CreatePriceSourceRequest = {
        ...newPriceSourceForm,
        nome: newPriceSourceForm.nome.trim(),
        allowedHosts: newPriceSourceForm.tipo === 'website' ? hosts : undefined,
        guidelineId:
          newPriceSourceForm.tipo === 'guideline' && newPriceSourceForm.guidelineId
            ? newPriceSourceForm.guidelineId.trim()
            : undefined,
      };
      const created = await apiService.createPriceSource(body);
      setPriceSources((prev) => [...prev, created].sort((a, b) => a.prioridade - b.prioridade));
      setNewPriceSourceForm({
        nome: '',
        tipo: 'website',
        prioridade: 0,
        ativo: true,
        allowedHosts: [],
        guidelineId: '',
      });
      setPriceHostsInput('');
      setPriceSourceGuidelineCombo('');
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'Erro ao criar fonte de preço');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePriceSource = async (id: string) => {
    if (!window.confirm('Remover esta fonte de preço?')) return;
    try {
      setActionLoading(true);
      await apiService.deletePriceSource(id);
      setPriceSources((prev) => prev.filter((p) => p.priceSourceId !== id));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao remover');
    } finally {
      setActionLoading(false);
    }
  };

  const parseHosts = (raw: string) =>
    raw
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);

  const handleCreateRagWebPage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWebPageForm.nome.trim() || !newWebPageForm.url.trim()) {
      alert('Nome e URL são obrigatórios');
      return;
    }
    const hosts = parseHosts(webPageHostsInput);
    if (!hosts.length) {
      alert('Informe pelo menos um host permitido (ex.: exemplo.com.br), um por linha ou separados por vírgula');
      return;
    }
    try {
      setActionLoading(true);
      const created = await apiService.createRagWebPage({
        nome: newWebPageForm.nome.trim(),
        url: newWebPageForm.url.trim(),
        allowedHosts: hosts,
        ativo: newWebPageForm.ativo !== false,
      });
      setRagWebPages((prev) => [...prev, created]);
      setNewWebPageForm({ nome: '', url: '', allowedHosts: [], ativo: true });
      setWebPageHostsInput('');
    } catch (err: any) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Erro ao criar página');
    } finally {
      setActionLoading(false);
    }
  };

  const handleIndexRagWebPage = async (page: RagWebPage) => {
    try {
      setActionLoading(true);
      await apiService.indexRagWebPage(page.pageId);
      const updated = await apiService.updateRagWebPage(page.pageId, {
        indexed: true,
        lastIndexedAt: new Date().toISOString(),
        lastIndexError: '',
      });
      setRagWebPages((prev) => prev.map((p) => (p.pageId === page.pageId ? updated : p)));
      alert('Página indexada no Pinecone.');
    } catch (err: any) {
      const data = err.response?.data as
        | { error?: string; message?: string; reason?: string; detail?: string }
        | undefined;
      const base =
        data?.error || data?.message || err.message || 'Falha ao indexar';
      const parts = [base];
      if (data?.reason) {
        parts.push(`Código: ${data.reason}`);
      }
      if (data?.detail) {
        parts.push(String(data.detail).slice(0, 800));
      }
      const msg = parts.join('\n\n');
      try {
        const failed = await apiService.updateRagWebPage(page.pageId, {
          indexed: false,
          lastIndexError: String(msg),
        });
        setRagWebPages((prev) => prev.map((p) => (p.pageId === page.pageId ? failed : p)));
      } catch {
        /* ignore */
      }
      alert(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRagWebPage = async (pageId: string) => {
    if (!window.confirm('Remover esta página e os vetores no Pinecone?')) return;
    try {
      setActionLoading(true);
      try {
        await apiService.deleteRagWebPageVectors(pageId);
      } catch {
        /* vectors may already be gone */
      }
      await apiService.deleteRagWebPage(pageId);
      setRagWebPages((prev) => prev.filter((p) => p.pageId !== pageId));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao remover página');
    } finally {
      setActionLoading(false);
    }
  };

  const selectedPrompt = useMemo(
    () => promptCatalog.find((p) => p.promptId === 'orchestrator') || null,
    [promptCatalog]
  );

  useEffect(() => {
    if (!selectedPrompt) return;
    setPromptEditor({
      overridePrompt: selectedPrompt.overridePrompt || '',
      examplesPositive: (selectedPrompt.examplesPositive || []).join('\n'),
      examplesNegative: (selectedPrompt.examplesNegative || []).join('\n'),
      threshold:
        typeof selectedPrompt.threshold === 'number' && Number.isFinite(selectedPrompt.threshold)
          ? String(selectedPrompt.threshold)
          : '',
    });
  }, [selectedPrompt]);

  const normalizeLines = (value: string): string[] =>
    value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  const handleSavePromptOverride = async () => {
    if (!selectedPrompt?.promptId) return;
    const thresholdValue = promptEditor.threshold.trim();
    const thresholdNumber = thresholdValue === '' ? null : Number(thresholdValue);
    if (
      thresholdNumber !== null &&
      (!Number.isFinite(thresholdNumber) || thresholdNumber < 0 || thresholdNumber > 1)
    ) {
      alert('Threshold deve estar entre 0 e 1.');
      return;
    }
    try {
      setActionLoading(true);
      const prompts = await apiService.saveOrchestratorPromptOverride(selectedPrompt.promptId, {
        overridePrompt: promptEditor.overridePrompt,
        examplesPositive: normalizeLines(promptEditor.examplesPositive),
        examplesNegative: normalizeLines(promptEditor.examplesNegative),
        threshold: thresholdNumber ?? undefined,
      });
      setPromptCatalog(prompts);
      alert('Override salvo com sucesso.');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao salvar override');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestorePromptDefault = async () => {
    if (!selectedPrompt?.promptId) return;
    if (!window.confirm('Remover override e voltar ao prompt padrão deste item?')) return;
    try {
      setActionLoading(true);
      const prompts = await apiService.restoreOrchestratorPromptDefault(selectedPrompt.promptId);
      setPromptCatalog(prompts);
      alert('Prompt restaurado para o padrão.');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao restaurar prompt');
    } finally {
      setActionLoading(false);
    }
  };

  const openCreateOverride = (base: SpecialistPrompt) => {
    setOverrideBase(base);
    setOverrideForm({
      name: `${base.name} (override org)`,
      description: `Override tenant para ${base.name}`,
      promptText: base.promptText || '',
    });
    setOverrideModalOpen(true);
  };

  const handleCreateOverride = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!overrideBase) return;
    if (!overrideForm.promptText.trim()) {
      alert('Prompt do override é obrigatório.');
      return;
    }
    try {
      setActionLoading(true);
      await apiService.createSpecialistPromptOverride({
        baseSpecialistId: overrideBase.specialistId,
        promptText: overrideForm.promptText,
        name: overrideForm.name,
        description: overrideForm.description,
      });
      const list = await apiService.getActiveSpecialistPrompts();
      setActiveSpecialists(list || []);
      setOverrideModalOpen(false);
      setOverrideBase(null);
      alert('Override criado com sucesso.');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao criar override');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="ai-settings-loading">
        <div className="spinner"></div>
        <p>Carregando configurações de IA...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-settings-error">
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadData}>
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <div className="ai-settings-tab">
      {/* OpenSpec #2.5 bridge — só renderiza banner enquanto a UI legacy
       * for o default. Quando IA_SETTINGS_UNIFIED_UI_ENABLED=true (default
       * pós unify-ia-settings Fase 1), o builder vive inline na tab
       * "Atendente IA & SDR" e o banner não tem mais sentido. */}
      {!IA_SETTINGS_UNIFIED_UI_ENABLED && (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            borderRadius: 8,
            background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)',
            border: '1px solid #c7d2fe',
            fontSize: '0.92rem',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 16 }}>✦</span>
          <div style={{ flex: 1 }}>
            <strong>Nova interface do Atendente IA</strong> está disponível. Editor em blocos, preview do prompt
            e abas para SDR Campos / Playground / Histórico.
          </div>
          <a
            href="/configuracao/ia/atendente-sdr"
            style={{
              padding: '6px 12px',
              background: '#6366f1',
              color: '#fff',
              borderRadius: 6,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Abrir nova UI →
          </a>
        </div>
      )}
      <div
        className="ai-decision-layers-hint"
        style={{
          marginBottom: 16,
          padding: '12px 14px',
          borderRadius: 8,
          background: 'var(--surface-elevated, #f4f6f9)',
          border: '1px solid var(--border-subtle, #e2e8f0)',
          fontSize: '0.95rem',
          lineHeight: 1.5,
        }}
      >
        <strong>Como a IA decide</strong> — três camadas:{' '}
        <strong>Identidade da IA</strong> (quem fala), <strong>Roteiro comercial</strong> (o que cobrir no diálogo) e{' '}
        <strong>Orquestrador / especialistas</strong> (sinais e decisão final). Especialistas emitem sinais; o orquestrador
        aplica a política e o handoff.
      </div>
      {/* Sub-tabs — quando IA_SETTINGS_UNIFIED_UI_ENABLED=true:
       *   - "Identidade da IA" e "SDR (Funil IA)" ocultas (legacy).
       *   - "Roteiro comercial" renomeado pra "Guidelines (RAG)".
       *   - Tab nova "Atendente IA & SDR" aparece pra canManageExtended. */}
      <nav className="ai-subtabs">
        <button
          className={`subtab ${activeSubTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('config')}
        >
          Motor &amp; fornecedor
        </button>
        {!IA_SETTINGS_UNIFIED_UI_ENABLED && (
          <button
            className={`subtab ${activeSubTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('roles')}
          >
            Identidade da IA
            <span className="subtab-badge">{roles.length}</span>
          </button>
        )}
        <button
          className={`subtab ${activeSubTab === 'guidelines' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('guidelines')}
        >
          {IA_SETTINGS_UNIFIED_UI_ENABLED ? 'Guidelines (RAG)' : 'Roteiro comercial'}
          <span className="subtab-badge">{guidelines.length}</span>
        </button>
        {CATALOG_RAG_ENABLED && (
          <button
            className={`subtab ${activeSubTab === 'catalog' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('catalog')}
          >
            📦 Catálogo
          </button>
        )}
        {canManageExtended && (
          <>
            <button
              className={`subtab ${activeSubTab === 'web_pages' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('web_pages')}
            >
              Páginas web (RAG)
              <span className="subtab-badge">{ragWebPages.length}</span>
            </button>
            <button
              className={`subtab ${activeSubTab === 'price_sources' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('price_sources')}
            >
              Fontes de preço
              <span className="subtab-badge">{priceSources.length}</span>
            </button>
            <button
              className={`subtab ${activeSubTab === 'orchestrator_prompts' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('orchestrator_prompts')}
            >
              Orquestrador (prompts)
            </button>
            {IA_SETTINGS_UNIFIED_UI_ENABLED ? (
              <button
                className={`subtab ${activeSubTab === 'atendente_ia_sdr' ? 'active' : ''}`}
                onClick={() => setActiveSubTab('atendente_ia_sdr')}
              >
                Atendente IA &amp; SDR
              </button>
            ) : (
              <button
                className={`subtab ${activeSubTab === 'sdr' ? 'active' : ''}`}
                onClick={() => setActiveSubTab('sdr')}
              >
                SDR (Funil IA)
              </button>
            )}
          </>
        )}
      </nav>

      {/* Config Sub-tab */}
      {activeSubTab === 'config' && (
        <div className="ai-subtab-content">
          <section className="settings-section">
            <h2>Motor &amp; fornecedor</h2>
            <p className="section-description">
              Provedor e modelo usados pelos fluxos de IA da organização. As{' '}
              <strong>sugestões no chat</strong> (botão no atendimento, endpoint{' '}
              <code>/ai/suggest</code>) só montam texto para o atendente revisar. A{' '}
              <strong>IA autônoma/orquestração</strong> (<code>/ai/orchestrate</code>) é outro fluxo no
              backend e não depende desse botão.
            </p>
            <p className="section-description" style={{ marginTop: 8 }}>
              <a
                href="https://github.com/marcelghisi/ghi-connect/blob/main/docs/guia-preenchimento-ia-llm.md"
                target="_blank"
                rel="noopener noreferrer"
              >
                Guia de preenchimento para equipas (LLM / prompts)
              </a>{' '}
              — boas práticas e exemplos no repositório.
            </p>

            <div className="settings-section" style={{ marginBottom: 24 }}>
              <h3 style={{ marginTop: 0 }}>Visualização do Funil IA</h3>
              <p className="section-description" style={{ marginBottom: 12 }}>
                Escolha como o módulo <strong>Funil IA</strong> no painel mostra leads em campanhas com modo Funil IA:{' '}
                <strong>Physics</strong> (bolinhas animadas por estágio) ou <strong>Slides</strong> (cartões por
                estágio).
              </p>
              <div className="ai-config-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="funil-visualizacao">Modo</label>
                    <select
                      id="funil-visualizacao"
                      value={editingFunilVisualizacao}
                      onChange={(e) => setEditingFunilVisualizacao(e.target.value as 'physics' | 'slides')}
                      disabled={!canManageFunilVisualizacao}
                    >
                      <option value="physics">Physics (bolinhas)</option>
                      <option value="slides">Slides (cartões)</option>
                    </select>
                  </div>
                </div>
                <div className="form-row" style={{ marginTop: 12 }}>
                  <div className="form-group">
                    <label htmlFor="funil-handoff-bins">Coluna de handoff (5 cartões)</label>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="funil-handoff-bins"
                        checked={editingHandoffBinsUi}
                        onChange={(e) => setEditingHandoffBinsUi(e.target.checked)}
                        disabled={!canManageFunilVisualizacao}
                      />
                      <label htmlFor="funil-handoff-bins" className="toggle-label">
                        {editingHandoffBinsUi ? 'Activada' : 'Desactivada'}
                      </label>
                    </div>
                    <p className="section-subtitle" style={{ marginTop: 8, marginBottom: 0 }}>
                      {editingHandoffBinsUi
                        ? 'Substitui os três alvos «Humano» na vista Physics por uma coluna comum com 5 cartões '
                          + '(atendimento humano, inactivo, low intent, opt-out comercial, tóxico — F4.5), contando '
                          + 'por '
                        : 'Comportamento legado: um alvo «Humano» por faixa. Ative para usar a coluna comum baseada em '}
                      <code>handoffBin</code>
                      {editingHandoffBinsUi ? ' da orquestração.' : '.'}
                    </p>
                  </div>
                </div>
                {canManageFunilVisualizacao && (
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => void handleSaveFunilVisualizacao()}
                      disabled={funilViewSaving}
                    >
                      {funilViewSaving ? 'Salvando…' : 'Salvar visualização'}
                    </button>
                  </div>
                )}
                {!canManageFunilVisualizacao && (
                  <p className="section-subtitle">Somente owner ou admin podem alterar esta opção.</p>
                )}
              </div>
            </div>

            <div className="ai-config-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Habilitar IA</label>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="ai-enabled"
                      checked={editingEnabled}
                      onChange={(e) => setEditingEnabled(e.target.checked)}
                      disabled={!isAdmin}
                    />
                    <label htmlFor="ai-enabled" className="toggle-label">
                      {editingEnabled ? 'Ativado' : 'Desativado'}
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Provedor</label>
                  <select
                    value={editingProvider}
                    onChange={(e) => {
                      const provider = e.target.value as AIProvider;
                      setEditingProvider(provider);
                      setEditingModel(getProviderModels(provider)[0]);
                    }}
                    disabled={!isAdmin}
                  >
                    <option value="openai">OpenAI</option>
                    <option value="azure">Azure OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                    <option value="google">Google AI</option>
                    <option value="bedrock">AWS Bedrock</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Modelo</label>
                  <select
                    value={editingModel}
                    onChange={(e) => setEditingModel(e.target.value)}
                    disabled={!isAdmin}
                  >
                    {getProviderModels(editingProvider).map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Temperatura: {editingTemperature}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={editingTemperature}
                    onChange={(e) => setEditingTemperature(parseFloat(e.target.value))}
                    disabled={!isAdmin}
                  />
                  <span className="form-hint">
                    Menor = mais preciso, Maior = mais criativo
                  </span>
                </div>

                <div className="form-group">
                  <label>Tom Padrão</label>
                  <select
                    value={editingTom}
                    onChange={(e) => setEditingTom(e.target.value as AITom)}
                    disabled={!isAdmin}
                  >
                    <option value="profissional">Profissional</option>
                    <option value="amigavel">Amigável</option>
                    <option value="formal">Formal</option>
                    <option value="casual">Casual</option>
                    <option value="tecnico">Técnico</option>
                  </select>
                </div>
                {/* OpenSpec #2.7 — per-tier provider/model overrides (opt-in) */}
                <PerTierConfigSection
                  value={editingProvidersByTier}
                  onChange={setEditingProvidersByTier}
                  canEdit={isAdmin || isOwner}
                />
              </div>

              {isAdmin && (
                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveSettings}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Salvando...' : 'Salvar Configurações'}
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {/* Roles Sub-tab — oculta quando IA_SETTINGS_UNIFIED_UI_ENABLED (legacy "Identidade da IA") */}
      {activeSubTab === 'roles' && !IA_SETTINGS_UNIFIED_UI_ENABLED && (
        <div className="ai-subtab-content">
          <section className="settings-section">
            <div className="section-header">
              <div>
                <h2>Identidade da IA</h2>
                <p className="section-description">
                  Perfis personalizados: quem a IA é e como fala em cada tipo de atendimento.
                </p>
                <details className="ai-profile-onboarding-hint" style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                    Como criar o perfil (livre ou guiado)
                  </summary>
                  <p style={{ marginTop: 8, fontSize: '0.95rem', lineHeight: 1.5 }}>
                    <strong>Opção A — livre:</strong> escreva nas instruções o que o negócio vende e como a IA deve
                    falar; pode usar o botão «Preencher com exemplo» no formulário.
                    <br />
                    <strong>Opção B — a partir de template:</strong> escolha um cartão abaixo e edite antes de
                    guardar. O perfil marcado como <strong>padrão</strong> é o que o <strong>orquestrador</strong>{' '}
                    usa na IA autônoma (combinado com o separador Prompts do orquestrador).
                  </p>
                </details>
              </div>
              {isAdmin && (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowAddRoleModal(true)}
                >
                  + Novo Perfil
                </button>
              )}
            </div>

            {defaultProfileForOrchestrator && (
              <div
                className="orchestrator-domain-preview"
                style={{
                  marginBottom: 20,
                  padding: 14,
                  borderRadius: 8,
                  background: 'var(--surface-muted, #f5f6fa)',
                  border: '1px solid var(--border-color, #e2e4ec)',
                }}
              >
                <h4 style={{ margin: '0 0 6px', fontSize: '1rem' }}>Pré-visualização — domínio no orquestrador</h4>
                <p style={{ margin: '0 0 10px', fontSize: '0.9rem', color: 'var(--text-secondary, #5c6478)' }}>
                  O perfil <strong>{defaultProfileForOrchestrator.nome}</strong> está em uso (padrão). O servidor
                  combina estas instruções com o pack em «Prompts do orquestrador» antes de chamar a IA autônoma.
                </p>
                <pre
                  style={{
                    margin: 0,
                    maxHeight: 140,
                    overflow: 'auto',
                    fontSize: '0.82rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {(defaultProfileForOrchestrator.systemPrompt || '').slice(0, 900)}
                  {(defaultProfileForOrchestrator.systemPrompt || '').length > 900 ? '…' : ''}
                </pre>
              </div>
            )}

            {/* Templates section */}
            {roleTemplates.length > 0 && (
              <div className="templates-section">
                <h3>Templates Disponíveis</h3>
                <div className="templates-grid">
                  {roleTemplates.map((template) => (
                    <div key={template.roleId} className="template-card">
                      <h4>{template.nome}</h4>
                      <p>{template.descricao}</p>
                      <span className="template-tom">{getTomLabel(template.tom)}</span>
                      {isAdmin && (
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleUseTemplate(template)}
                        >
                          Usar Template
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Roles list */}
            {roles.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">🤖</span>
                <h3>Nenhum perfil de IA</h3>
                <p>Crie um perfil personalizado ou use um template para começar.</p>
              </div>
            ) : (
              <div className="roles-list">
                {roles.map((role) => (
                  <div key={role.roleId} className="role-card">
                    <div className="role-header">
                      <div className="role-info">
                        <h3>
                          {role.nome}
                          {role.isDefault && (
                            <span className="default-badge" title="Perfil em uso pelo orquestrador (IA autônoma)">
                              Padrão
                            </span>
                          )}
                        </h3>
                        <p>{role.descricao}</p>
                      </div>
                      <span className="role-tom">{getTomLabel(role.tom)}</span>
                    </div>
                    {role.exemplos && role.exemplos.length > 0 && (
                      <div className="role-examples">
                        <span className="examples-label">
                          {role.exemplos.length} exemplo(s) configurado(s)
                        </span>
                      </div>
                    )}
                    {isAdmin && (
                      <div className="role-actions">
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleEditRole(role)}
                        >
                          Editar
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteRole(role.roleId)}
                          disabled={actionLoading}
                        >
                          Excluir
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* Roteiro comercial (guidelines) Sub-tab */}
      {activeSubTab === 'guidelines' && (
        <div className="ai-subtab-content">
          <section className="settings-section">
            <div className="section-header">
              <div>
                <h2>Roteiro comercial</h2>
                <p className="section-description">
                  Guidelines e regras para orientar o diálogo comercial da IA.
                </p>
              </div>
              {isAdmin && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setGuidelineFile(null);
                    setAddGuidelineFileInputKey((k) => k + 1);
                    setShowAddGuidelineModal(true);
                  }}
                >
                  + Novo Guideline
                </button>
              )}
            </div>

            {guidelines.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">📚</span>
                <h3>Nenhum guideline</h3>
                <p>Adicione documentos para melhorar as sugestões da IA.</p>
              </div>
            ) : (
              <div className="guidelines-list">
                {guidelines.map((guideline) => (
                  <div key={guideline.guidelineId} className="guideline-card">
                    <div className="guideline-header">
                      <div className="guideline-info">
                        <h3>{guideline.titulo}</h3>
                        <span className={`guideline-tipo tipo-${guideline.tipo}`}>
                          {getTipoLabel(guideline.tipo)}
                        </span>
                      </div>
                      <span className={`index-status ${guideline.indexed ? 'indexed' : 'pending'}`}>
                        {guideline.indexed ? 'Indexado' : 'Pendente'}
                      </span>
                    </div>
                    {guideline.conteudo && (
                      <p className="guideline-preview">
                        {guideline.conteudo.substring(0, 200)}
                        {guideline.conteudo.length > 200 ? '...' : ''}
                      </p>
                    )}
                    <div className="guideline-meta">
                      <span className="meta-date">
                        Atualizado: {new Date(guideline.updatedAt).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    {isAdmin && (
                      <div className="guideline-actions">
                        {!guideline.indexed && (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => handleRetryIndexGuideline(guideline.guidelineId)}
                            disabled={actionLoading}
                          >
                            Indexar agora
                          </button>
                        )}
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleEditGuideline(guideline)}
                        >
                          Editar
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteGuideline(guideline.guidelineId)}
                          disabled={actionLoading}
                        >
                          Excluir
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeSubTab === 'web_pages' && canManageExtended && (
        <div className="ai-subtab-content">
          <section className="settings-section">
            <h2>Páginas web para RAG</h2>
            <p className="section-description">
              Cadastre URLs com allowlist de hosts. Depois use <strong>Indexar no Pinecone</strong> para
              incorporar o texto ao mesmo índice das guidelines (sugestões e orquestração). Páginas muito
              dinâmicas (só JavaScript) podem indexar vazio.
            </p>
            <form onSubmit={handleCreateRagWebPage} className="ai-config-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    value={newWebPageForm.nome}
                    onChange={(e) => setNewWebPageForm({ ...newWebPageForm, nome: e.target.value })}
                    placeholder="Ex.: Página de cursos"
                    disabled={actionLoading}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>URL</label>
                  <input
                    type="url"
                    value={newWebPageForm.url}
                    onChange={(e) => setNewWebPageForm({ ...newWebPageForm, url: e.target.value })}
                    placeholder="https://..."
                    disabled={actionLoading}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Hosts permitidos (obrigatório)</label>
                <textarea
                  value={webPageHostsInput}
                  onChange={(e) => setWebPageHostsInput(e.target.value)}
                  placeholder={"exemplo.com.br\nwww.exemplo.com.br"}
                  rows={3}
                  disabled={actionLoading}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                Adicionar página
              </button>
            </form>
            {ragWebPages.length === 0 ? (
              <p style={{ marginTop: 24 }}>Nenhuma página cadastrada.</p>
            ) : (
              <div className="guidelines-list" style={{ marginTop: 24 }}>
                {ragWebPages.map((p) => (
                  <div key={p.pageId} className="guideline-card">
                    <div className="guideline-header">
                      <div className="guideline-info">
                        <h3>{p.nome}</h3>
                        <a href={p.url} target="_blank" rel="noopener noreferrer">
                          {p.url}
                        </a>
                      </div>
                      <span className={`index-status ${p.indexed ? 'indexed' : 'pending'}`}>
                        {p.indexed ? 'Indexado' : 'Pendente'}
                      </span>
                    </div>
                    <p className="guideline-preview">
                      Hosts: {(p.allowedHosts || []).join(', ') || '—'}
                    </p>
                    {p.lastIndexError && (
                      <p className="section-description" style={{ color: '#c0392b' }}>
                        Último erro: {p.lastIndexError}
                      </p>
                    )}
                    <div className="guideline-actions">
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => handleIndexRagWebPage(p)}
                        disabled={actionLoading || p.ativo === false}
                      >
                        Indexar no Pinecone
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteRagWebPage(p.pageId)}
                        disabled={actionLoading}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeSubTab === 'price_sources' && canManageExtended && (
        <div className="ai-subtab-content">
          <section className="settings-section">
            <h2>Fontes de preço</h2>
            <p className="section-description">
              Allowlist para o agente de preços: sites (hosts) ou guidelines já indexadas. Ordem por prioridade
              (número menor primeiro).
            </p>

            <form onSubmit={handleCreatePriceSource} className="ai-config-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    value={newPriceSourceForm.nome}
                    onChange={(e) => setNewPriceSourceForm({ ...newPriceSourceForm, nome: e.target.value })}
                    placeholder="Ex.: Site institucional"
                    disabled={actionLoading}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Tipo</label>
                  <select
                    value={newPriceSourceForm.tipo}
                    onChange={(e) => {
                      const tipo = e.target.value as CreatePriceSourceRequest['tipo'];
                      setNewPriceSourceForm({
                        ...newPriceSourceForm,
                        tipo,
                        guidelineId: tipo === 'guideline' ? newPriceSourceForm.guidelineId : '',
                      });
                      if (tipo !== 'guideline') {
                        setPriceSourceGuidelineCombo('');
                      }
                    }}
                    disabled={actionLoading}
                  >
                    <option value="website">Website</option>
                    <option value="guideline">Guideline</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Prioridade</label>
                  <input
                    type="number"
                    value={newPriceSourceForm.prioridade ?? 0}
                    onChange={(e) =>
                      setNewPriceSourceForm({
                        ...newPriceSourceForm,
                        prioridade: Number(e.target.value) || 0,
                      })
                    }
                    disabled={actionLoading}
                  />
                </div>
              </div>
              {newPriceSourceForm.tipo === 'website' ? (
                <div className="form-group">
                  <label>Hosts permitidos (vírgula ou linha)</label>
                  <textarea
                    value={priceHostsInput}
                    onChange={(e) => setPriceHostsInput(e.target.value)}
                    placeholder="exemplo.com.br&#10;www.outro.com"
                    rows={3}
                    disabled={actionLoading}
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Guideline (por nome)</label>
                  <div className="guideline-autocomplete" ref={priceSourceGuidelineWrapRef}>
                    <input
                      type="text"
                      autoComplete="off"
                      role="combobox"
                      value={priceSourceGuidelineCombo}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPriceSourceGuidelineCombo(v);
                        setNewPriceSourceForm((f) => ({ ...f, guidelineId: '' }));
                        setPriceSourceGuidelineListOpen(true);
                      }}
                      onFocus={() => setPriceSourceGuidelineListOpen(true)}
                      placeholder={
                        guidelines.length === 0
                          ? 'Nenhuma guideline cadastrada — crie na aba Guidelines'
                          : 'Digite para filtrar ou clique para listar'
                      }
                      disabled={actionLoading || guidelines.length === 0}
                      aria-expanded={priceSourceGuidelineListOpen}
                      aria-controls="price-source-guideline-listbox"
                      aria-autocomplete="list"
                      aria-haspopup="listbox"
                    />
                    {priceSourceGuidelineListOpen && guidelines.length > 0 && (
                      <ul
                        id="price-source-guideline-listbox"
                        className="guideline-autocomplete-list"
                        role="listbox"
                      >
                        {priceSourceGuidelineOptions.length === 0 ? (
                          <li className="guideline-autocomplete-empty" role="presentation">
                            Nenhum título corresponde.
                          </li>
                        ) : (
                          priceSourceGuidelineOptions.map((g) => (
                            <li
                              key={g.guidelineId}
                              role="option"
                              aria-selected={newPriceSourceForm.guidelineId === g.guidelineId}
                            >
                              <button
                                type="button"
                                className="guideline-autocomplete-option"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectPriceSourceGuideline(g)}
                              >
                                <span className="guideline-autocomplete-title">{g.titulo}</span>
                                <span className="guideline-autocomplete-meta">
                                  {getTipoLabel(g.tipo)}
                                  {g.indexed ? ' · Indexado' : ' · Pendente'}
                                </span>
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>
                  {newPriceSourceForm.guidelineId ? (
                    <p className="form-hint guideline-id-hint">
                      ID interno: <code>{newPriceSourceForm.guidelineId}</code>
                    </p>
                  ) : null}
                </div>
              )}
              <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                Adicionar fonte
              </button>
            </form>

            {priceSources.length === 0 ? (
              <div className="empty-state">
                <p>Nenhuma fonte cadastrada. A IA usará só guidelines de preço (RAG) quando existirem.</p>
              </div>
            ) : (
              <div className="guidelines-list" style={{ marginTop: 24 }}>
                {priceSources.map((ps) => (
                  <div key={ps.priceSourceId} className="guideline-card">
                    <div className="guideline-header">
                      <div className="guideline-info">
                        <h3>{ps.nome}</h3>
                        <span className={`guideline-tipo tipo-${ps.tipo}`}>{ps.tipo}</span>
                      </div>
                      <span className="meta-date">Prioridade: {ps.prioridade}</span>
                    </div>
                    <p className="guideline-preview">
                      {ps.tipo === 'website'
                        ? (ps.allowedHosts || []).join(', ') || '—'
                        : `Guideline: ${ps.guidelineId || '—'}`}
                    </p>
                    <div className="guideline-actions">
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeletePriceSource(ps.priceSourceId)}
                        disabled={actionLoading}
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeSubTab === 'orchestrator_prompts' && canManageExtended && (
        <div className="ai-subtab-content">
          <section className="settings-section">
            <h2>Orquestrador — catálogo de prompts</h2>
            <p className="section-description">
              Baseline do produto em somente leitura; override por organização; restaurar padrão quando necessário.
              A decisão final de política combina estes prompts com sinais dos especialistas.
            </p>
            <div className="form-actions" style={{ marginBottom: 12 }}>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setOrchestratorHelpDrawerOpen(true)}
              >
                Ver exemplos (guia rápido)
              </button>
            </div>
            <div className="ai-config-form" style={{ marginBottom: 12 }}>
              <h3 style={{ marginTop: 0 }}>
                {selectedPrompt?.title || 'Orquestrador principal'}
              </h3>
              <p className="section-description" style={{ marginBottom: 10 }}>
                Este é o único prompt especial. Ajuste o override da sua organização e mantenha especialistas no bloco
                abaixo.
              </p>
              {!selectedPrompt ? (
                <p className="section-description">Prompt do orquestrador indisponível no catálogo.</p>
              ) : (
                <>
                  <div className="form-group">
                    <label>Prompt default (readonly)</label>
                    <textarea value={selectedPrompt.defaultReadonly} rows={5} disabled readOnly />
                  </div>
                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <label style={{ margin: 0 }}>Override da organização</label>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => setOrchestratorHelpDrawerOpen(true)}
                      >
                        Ver exemplos
                      </button>
                    </div>
                    <textarea
                      value={promptEditor.overridePrompt}
                      onChange={(e) => setPromptEditor((p) => ({ ...p, overridePrompt: e.target.value }))}
                      rows={5}
                      disabled={actionLoading}
                      title={orchestratorHelpPtBR.overrideHint}
                      aria-describedby="orchestrator-override-hint"
                    />
                    <span id="orchestrator-override-hint" className="form-hint" style={{ display: 'block', marginTop: 6 }}>
                      {orchestratorHelpPtBR.overrideHint}
                    </span>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label title={orchestratorHelpPtBR.examplesPositiveHint}>
                        Exemplos positivos (um por linha)
                      </label>
                      <textarea
                        value={promptEditor.examplesPositive}
                        onChange={(e) => setPromptEditor((p) => ({ ...p, examplesPositive: e.target.value }))}
                        rows={4}
                        disabled={actionLoading}
                        title={orchestratorHelpPtBR.examplesPositiveHint}
                      />
                      <span className="form-hint">{orchestratorHelpPtBR.examplesPositiveHint}</span>
                    </div>
                    <div className="form-group">
                      <label title={orchestratorHelpPtBR.examplesNegativeHint}>
                        Exemplos negativos (um por linha)
                      </label>
                      <textarea
                        value={promptEditor.examplesNegative}
                        onChange={(e) => setPromptEditor((p) => ({ ...p, examplesNegative: e.target.value }))}
                        rows={4}
                        disabled={actionLoading}
                        title={orchestratorHelpPtBR.examplesNegativeHint}
                      />
                      <span className="form-hint">{orchestratorHelpPtBR.examplesNegativeHint}</span>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label title={orchestratorHelpPtBR.thresholdHint}>Threshold (0 a 1)</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={promptEditor.threshold}
                        onChange={(e) => setPromptEditor((p) => ({ ...p, threshold: e.target.value }))}
                        disabled={actionLoading}
                        title={orchestratorHelpPtBR.thresholdHint}
                      />
                      <span className="form-hint">{orchestratorHelpPtBR.thresholdHint}</span>
                    </div>
                    <div className="form-group">
                      <label>Schema esperado</label>
                      <textarea value={selectedPrompt.outputSchema || '—'} rows={3} disabled readOnly />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Preview final (perfil + override + SDR — igual ao enviado ao ai-service)</label>
                    <textarea value={selectedPrompt.mergedPreview} rows={8} disabled readOnly />
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSavePromptOverride}
                      disabled={actionLoading}
                    >
                      Salvar override
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={handleRestorePromptDefault}
                      disabled={actionLoading}
                    >
                      Voltar ao padrão
                    </button>
                  </div>
                </>
              )}
            </div>

            {promptCatalog.filter((p) => p.tenantOverridable === false).length > 0 ? (
              <div className="ai-config-form" style={{ marginBottom: 12 }}>
                <h3 style={{ marginTop: 0 }}>Prompts de classificadores (readonly)</h3>
                <p className="section-description" style={{ marginBottom: 10 }}>
                  Estes prompts são editáveis apenas pelo super-admin (via baseline do produto). A
                  customização vertical da sua organização vai por SDR, perfil de IA e specialists.
                </p>
                <div className="guidelines-list">
                  {promptCatalog
                    .filter((p) => p.tenantOverridable === false)
                    .map((p) => (
                      <div key={p.promptId} className="guideline-card">
                        <div className="guideline-header">
                          <div className="guideline-info">
                            <h3>{p.title}</h3>
                            <span className="guideline-tipo tipo-global">super-admin</span>
                          </div>
                          <span className="index-status pending">readonly</span>
                        </div>
                        <p className="guideline-preview">{p.description || '—'}</p>
                        <details>
                          <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666' }}>
                            Ver prompt
                          </summary>
                          <textarea
                            value={p.defaultReadonly}
                            rows={8}
                            disabled
                            readOnly
                            style={{ marginTop: 6, width: '100%', fontFamily: 'monospace', fontSize: 12 }}
                          />
                        </details>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}

            <div className="ai-config-form" style={{ marginBottom: 12 }}>
              <h3 style={{ marginTop: 0 }}>Especialistas ativos (runtime)</h3>
              <p className="section-description" style={{ marginBottom: 10 }}>
                Nativos aparecem em readonly para gestão da organização. Super Admin mantém CRUD completo no módulo
                próprio.
              </p>
              <div className="guidelines-list">
                {activeSpecialists.map((s) => (
                  <div key={s.specialistId} className="guideline-card">
                    <div className="guideline-header">
                      <div className="guideline-info">
                        <h3>{s.name}</h3>
                        <span className={`guideline-tipo tipo-${s.scope}`}>{s.scope}</span>
                      </div>
                      {s.readonly ? <span className="index-status pending">readonly</span> : null}
                    </div>
                    <p className="guideline-preview">{s.description || '—'}</p>
                    <p className="guideline-preview">
                      <strong>Contrato:</strong> {s.returnContract}
                    </p>
                    <div className="guideline-actions">
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => openCreateOverride(s)}
                        disabled={actionLoading || !s.readonly}
                        title={s.readonly ? 'Criar versão tenant editável para a sua organização' : 'Já é editável'}
                      >
                        Criar override
                      </button>
                    </div>
                  </div>
                ))}
                {activeSpecialists.length === 0 ? (
                  <p className="section-description">Nenhum especialista ativo encontrado.</p>
                ) : null}
              </div>
            </div>
          </section>
        </div>
      )}

      {activeSubTab === 'sdr' && canManageExtended && aiSettings && !IA_SETTINGS_UNIFIED_UI_ENABLED && (
        <SdrSettingsPanel
          key={organizationId}
          organizationId={organizationId}
          aiSettings={aiSettings}
          onSaved={setAiSettings}
          canEdit={canEditSdr}
          busy={actionLoading}
          setBusy={setActionLoading}
        />
      )}

      {activeSubTab === 'atendente_ia_sdr' && canManageExtended && IA_SETTINGS_UNIFIED_UI_ENABLED && (
        <AtendenteIaInlineTab canEdit={canEditSdr} />
      )}

      {activeSubTab === 'catalog' && CATALOG_RAG_ENABLED && (
        <CatalogTab canEdit={canEditSdr} />
      )}

      {/* Add Role Modal */}
      {showAddRoleModal && (
        <div className="modal-overlay" onClick={() => setShowAddRoleModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Novo Perfil de IA</h3>
              <button className="modal-close" onClick={() => setShowAddRoleModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateRole}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    value={newRoleForm.nome}
                    onChange={(e) => setNewRoleForm({ ...newRoleForm, nome: e.target.value })}
                    placeholder="Ex: Vendedor de Cursos"
                    required
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Descrição</label>
                  <input
                    type="text"
                    value={newRoleForm.descricao}
                    onChange={(e) => setNewRoleForm({ ...newRoleForm, descricao: e.target.value })}
                    placeholder="Uma breve descrição do perfil"
                    required
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Instruções para a IA</label>
                  <div className="form-actions" style={{ marginBottom: 8 }}>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() =>
                        setNewRoleForm({
                          ...newRoleForm,
                          systemPrompt: profileDomainExampleFromCatalog,
                        })
                      }
                      disabled={actionLoading || !profileDomainExampleFromCatalog}
                      title={
                        profileDomainExampleFromCatalog
                          ? undefined
                          : 'Exemplo indisponível — verifique permissões ou o catálogo de prompts.'
                      }
                    >
                      Preencher com exemplo (vendas educacionais)
                    </button>
                  </div>
                  <textarea
                    value={newRoleForm.systemPrompt}
                    onChange={(e) => setNewRoleForm({ ...newRoleForm, systemPrompt: e.target.value })}
                    placeholder="Descreva como a IA deve se comportar, qual seu objetivo, como abordar o cliente..."
                    rows={5}
                    required
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Tom</label>
                    <select
                      value={newRoleForm.tom}
                      onChange={(e) =>
                        setNewRoleForm({ ...newRoleForm, tom: e.target.value as AITom })
                      }
                      disabled={actionLoading}
                    >
                      <option value="profissional">Profissional</option>
                      <option value="amigavel">Amigável</option>
                      <option value="formal">Formal</option>
                      <option value="casual">Casual</option>
                      <option value="tecnico">Técnico</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newRoleForm.isDefault}
                        onChange={(e) =>
                          setNewRoleForm({ ...newRoleForm, isDefault: e.target.checked })
                        }
                        disabled={actionLoading}
                      />
                      Definir como padrão
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowAddRoleModal(false)}
                  disabled={actionLoading}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Criando...' : 'Criar Perfil'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditRoleModal && selectedRole && (
        <div className="modal-overlay" onClick={() => setShowEditRoleModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar Perfil de IA</h3>
              <button className="modal-close" onClick={() => setShowEditRoleModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateRole}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    value={newRoleForm.nome}
                    onChange={(e) => setNewRoleForm({ ...newRoleForm, nome: e.target.value })}
                    required
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Descrição</label>
                  <input
                    type="text"
                    value={newRoleForm.descricao}
                    onChange={(e) => setNewRoleForm({ ...newRoleForm, descricao: e.target.value })}
                    required
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Instruções para a IA</label>
                  <div className="form-actions" style={{ marginBottom: 8 }}>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() =>
                        setNewRoleForm({
                          ...newRoleForm,
                          systemPrompt: profileDomainExampleFromCatalog,
                        })
                      }
                      disabled={actionLoading || !profileDomainExampleFromCatalog}
                      title={
                        profileDomainExampleFromCatalog
                          ? undefined
                          : 'Exemplo indisponível — verifique permissões ou o catálogo de prompts.'
                      }
                    >
                      Preencher com exemplo (vendas educacionais)
                    </button>
                  </div>
                  <textarea
                    value={newRoleForm.systemPrompt}
                    onChange={(e) => setNewRoleForm({ ...newRoleForm, systemPrompt: e.target.value })}
                    rows={5}
                    required
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Tom</label>
                    <select
                      value={newRoleForm.tom}
                      onChange={(e) =>
                        setNewRoleForm({ ...newRoleForm, tom: e.target.value as AITom })
                      }
                      disabled={actionLoading}
                    >
                      <option value="profissional">Profissional</option>
                      <option value="amigavel">Amigável</option>
                      <option value="formal">Formal</option>
                      <option value="casual">Casual</option>
                      <option value="tecnico">Técnico</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newRoleForm.isDefault}
                        onChange={(e) =>
                          setNewRoleForm({ ...newRoleForm, isDefault: e.target.checked })
                        }
                        disabled={actionLoading}
                      />
                      Definir como padrão
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowEditRoleModal(false)}
                  disabled={actionLoading}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Guideline Modal */}
      {showAddGuidelineModal && (
        <div className="modal-overlay" onClick={() => setShowAddGuidelineModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Novo Guideline</h3>
              <button className="modal-close" onClick={() => setShowAddGuidelineModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateGuideline}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Título</label>
                    <input
                      type="text"
                      value={newGuidelineForm.titulo}
                      onChange={(e) =>
                        setNewGuidelineForm({ ...newGuidelineForm, titulo: e.target.value })
                      }
                      placeholder="Ex: Manual de Atendimento"
                      required
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label>Tipo</label>
                    <select
                      value={newGuidelineForm.tipo}
                      onChange={(e) =>
                        setNewGuidelineForm({
                          ...newGuidelineForm,
                          tipo: e.target.value as GuidelineTipo,
                        })
                      }
                      disabled={actionLoading}
                    >
                      <option value="atendimento">Atendimento</option>
                      <option value="produto">Produto</option>
                      <option value="curso">Curso</option>
                      <option value="preco">Preço</option>
                      <option value="servico">Serviço</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Upload de Arquivo (opcional)</label>
                  <input
                    key={addGuidelineFileInputKey}
                    type="file"
                    accept=".txt,.pdf,.docx"
                    onChange={(e) => setGuidelineFile(e.target.files?.[0] || null)}
                    disabled={actionLoading}
                  />
                  <span className="form-hint">
                    Formatos aceitos: TXT, PDF, DOCX
                  </span>
                </div>

                <div className="form-group">
                  <label>Conteúdo</label>
                  <textarea
                    value={newGuidelineForm.conteudo}
                    onChange={(e) =>
                      setNewGuidelineForm({ ...newGuidelineForm, conteudo: e.target.value })
                    }
                    placeholder="Digite o conteúdo do guideline ou faça upload de um arquivo..."
                    rows={10}
                    disabled={actionLoading || !!guidelineFile}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setShowAddGuidelineModal(false);
                    setGuidelineFile(null);
                  }}
                  disabled={actionLoading}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Criando...' : 'Criar Guideline'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Guideline Modal */}
      {showEditGuidelineModal && selectedGuideline && (
        <div className="modal-overlay" onClick={() => setShowEditGuidelineModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar Guideline</h3>
              <button className="modal-close" onClick={() => setShowEditGuidelineModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateGuideline}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Título</label>
                    <input
                      type="text"
                      value={newGuidelineForm.titulo}
                      onChange={(e) =>
                        setNewGuidelineForm({ ...newGuidelineForm, titulo: e.target.value })
                      }
                      required
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label>Tipo</label>
                    <select
                      value={newGuidelineForm.tipo}
                      onChange={(e) =>
                        setNewGuidelineForm({
                          ...newGuidelineForm,
                          tipo: e.target.value as GuidelineTipo,
                        })
                      }
                      disabled={actionLoading}
                    >
                      <option value="atendimento">Atendimento</option>
                      <option value="produto">Produto</option>
                      <option value="curso">Curso</option>
                      <option value="preco">Preço</option>
                      <option value="servico">Serviço</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Substituir arquivo (opcional)</label>
                  <input
                    key={editGuidelineFileInputKey}
                    type="file"
                    accept=".txt,.pdf,.docx"
                    onChange={(e) => setGuidelineFile(e.target.files?.[0] || null)}
                    disabled={actionLoading}
                  />
                </div>

                <div className="form-group">
                  <label>Conteúdo</label>
                  <textarea
                    value={newGuidelineForm.conteudo}
                    onChange={(e) =>
                      setNewGuidelineForm({ ...newGuidelineForm, conteudo: e.target.value })
                    }
                    rows={10}
                    disabled={actionLoading || !!guidelineFile}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setShowEditGuidelineModal(false);
                    setSelectedGuideline(null);
                    setGuidelineFile(null);
                  }}
                  disabled={actionLoading}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {orchestratorHelpDrawerOpen ? (
        <div
          className="ai-help-drawer-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOrchestratorHelpDrawerOpen(false);
          }}
        >
          <aside
            className="ai-help-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-orchestrator-help-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="ai-help-drawer-header">
              <span id="ai-orchestrator-help-title">{orchestratorHelpPtBR.drawerTitle}</span>
              <button
                type="button"
                className="modal-close"
                aria-label="Fechar painel de ajuda"
                onClick={() => setOrchestratorHelpDrawerOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ai-help-drawer-body">{orchestratorHelpPtBR.drawerBody}</div>
          </aside>
        </div>
      ) : null}

      {overrideModalOpen && overrideBase ? (
        <div className="modal-overlay" onClick={() => setOverrideModalOpen(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Criar override (tenant)</h3>
              <button className="modal-close" onClick={() => setOverrideModalOpen(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateOverride}>
              <div className="modal-body">
                <p className="section-description">
                  Base: <strong>{overrideBase.name}</strong> ({overrideBase.returnContract})
                </p>
                <div className="form-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    value={overrideForm.name}
                    onChange={(e) => setOverrideForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Descrição</label>
                  <input
                    type="text"
                    value={overrideForm.description}
                    onChange={(e) => setOverrideForm((f) => ({ ...f, description: e.target.value }))}
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Prompt do override</label>
                  <textarea
                    rows={7}
                    value={overrideForm.promptText}
                    onChange={(e) => setOverrideForm((f) => ({ ...f, promptText: e.target.value }))}
                    disabled={actionLoading}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setOverrideModalOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Criando...' : 'Criar override'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};
